import { useEffect, useState } from "react";
import supabase from "../supabaseClient";

const MINIMUM_LOADING_MS = 350;

export default function PrivacyGate({ children }) {
  const [retryKey, setRetryKey] = useState(0);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [hasReadNotice, setHasReadNotice] = useState(false);
  const [acknowledgementError, setAcknowledgementError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadPrivacyStatus() {
      const startedAt = Date.now();
      setIsLoading(true);
      setLoadError("");
      try {
        const sessionResult = await supabase.auth.getSession();
        if (!active) return;
        if (sessionResult.error) {
          setLoadError(sessionResult.error.message || "Sessie controleren is mislukt.");
          return;
        }
        if (!sessionResult.data?.session) return;

        const result = await supabase.privacy.getStatus();
        if (!active) return;
        if (result.error) {
          setLoadError(result.error.message || "Privacyverklaringstatus laden mislukt.");
        } else {
          setStatus(result.data);
        }
      } finally {
        const remaining = MINIMUM_LOADING_MS - (Date.now() - startedAt);
        if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
        if (active) setIsLoading(false);
      }
    }

    loadPrivacyStatus().catch((error) => {
      if (active) {
        setLoadError(error?.message || "Privacyverklaringstatus laden mislukt.");
        setIsLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [retryKey]);

  async function handleAcknowledge(event) {
    event.preventDefault();
    if (!hasReadNotice || isSaving) return;

    setIsSaving(true);
    setAcknowledgementError("");
    try {
      const result = await supabase.privacy.acknowledge();
      if (result.error) throw new Error(result.error.message || "Bevestiging opslaan mislukt.");
      setStatus((current) => ({
        ...current,
        acknowledged: result.data.acknowledged,
        version: result.data.version,
      }));
    } catch (error) {
      setAcknowledgementError(error?.message || "Bevestiging opslaan mislukt.");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading || (!status && !loadError)) {
    return (
      <main className="ct-privacy-page" aria-live="polite">
        <div className="ct-privacy-card ct-privacy-loading">Privacyverklaring laden...</div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="ct-privacy-page">
        <section className="ct-privacy-card" role="alert">
          <p className="ct-privacy-eyebrow">ClassroomTasks</p>
          <h1 className="ct-privacy-title">Privacyverklaring laden mislukt</h1>
          <p className="ct-privacy-error">{loadError}</p>
          <button className="ct-privacy-submit" onClick={() => setRetryKey((key) => key + 1)}>
            Opnieuw proberen
          </button>
        </section>
      </main>
    );
  }

  if (status?.acknowledged || !status?.required) return children;

  return (
    <main className="ct-privacy-page">
      <section className="ct-privacy-card">
        <p className="ct-privacy-eyebrow">ClassroomTasks</p>
        <h1 className="ct-privacy-title">Privacy en persoonsgegevens</h1>
        <p className="ct-privacy-copy">
          ClassroomTasks verwerkt persoonsgegevens van leerkrachten en leerlingen om taken,
          voortgang en feedback voor de klas te beheren.
        </p>
        <p className="ct-privacy-copy">
          Lees de privacyverklaring om te zien welke gegevens worden verwerkt, waarom dit gebeurt,
          hoe lang gegevens worden bewaard en welke rechten je hebt.
        </p>
        <a
          className="ct-privacy-link"
          href={status.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacyverklaring bekijken
        </a>
        <form className="ct-privacy-form" onSubmit={handleAcknowledge}>
          <label className="ct-privacy-check-row">
            <input
              type="checkbox"
              checked={hasReadNotice}
              onChange={(event) => setHasReadNotice(event.target.checked)}
            />
            <span>Ik heb de privacyverklaring gelezen.</span>
          </label>
          {acknowledgementError && <p className="ct-privacy-error" role="alert">{acknowledgementError}</p>}
          <button className="ct-privacy-submit" type="submit" disabled={!hasReadNotice || isSaving}>
            {isSaving ? "Bevestiging opslaan..." : "Doorgaan"}
          </button>
        </form>
      </section>
    </main>
  );
}
